import posthog from "posthog-js";
import { logger } from "./LoggerService";
/**
 * PostHog Back Office Service
 * Singleton service for PostHog analytics integration
 * Only initializes on Back Office routes (authenticated routes)
 */
export class PostHogBOService {
    constructor() {
        Object.defineProperty(this, "posthogInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "_isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Private constructor for singleton pattern
    }
    static getInstance() {
        if (!PostHogBOService.instance) {
            PostHogBOService.instance = new PostHogBOService();
        }
        return PostHogBOService.instance;
    }
    /**
     * Initialize PostHog with configuration
     */
    initialize(config) {
        if (this._isInitialized) {
            logger.debug("PostHog already initialized");
            return;
        }
        if (!config || !config.host || !config.apiKey) {
            logger.debug("PostHog not initialized: missing configuration");
            return;
        }
        try {
            posthog.init(config.apiKey, {
                api_host: config.host,
                session_recording: {
                    recordCrossOriginIframes: false,
                },
                disable_session_recording: false, // Explicitly enable session recording
                enable_recording_console_log: true, // Record console logs in session replay
                capture_pageview: false, // We'll capture pageviews manually via router
                capture_pageleave: true,
                autocapture: true,
                capture_exceptions: true, // Enable automatic error tracking
                enable_heatmaps: true,
                rageclick: true,
                loaded: (posthogInstance) => {
                    logger.debug("PostHog loaded successfully");
                    this.posthogInstance = posthogInstance;
                    this._isInitialized = true;
                    // Expose for debugging in dev mode
                    if (import.meta.env.DEV) {
                        window.posthog = posthogInstance;
                    }
                    // Try to start session recording explicitly
                    posthogInstance.startSessionRecording();
                },
            });
            logger.debug("PostHog initialization started");
        }
        catch (error) {
            logger.debug("Failed to initialize PostHog:", error);
            // Don't throw - we don't want to break the app if PostHog fails
        }
    }
    /**
     * Identify user with their properties (called when user authenticates)
     * @param userId - User identifier (sub or email)
     * @param roles - Array of user roles
     */
    identifyUser(userId, roles) {
        if (!this._isInitialized || !this.posthogInstance) {
            logger.debug("PostHog not initialized, cannot identify user");
            return;
        }
        if (!userId) {
            logger.debug("Invalid userId, skipping PostHog identification");
            return;
        }
        try {
            // Identify user with roles as person properties
            this.posthogInstance.identify(userId, { roles });
            // Also register roles as super properties so they appear in every event
            this.posthogInstance.register({ user_roles: roles });
            logger.debug("PostHog user identified:", userId, "with roles:", roles);
        }
        catch (error) {
            logger.debug("Failed to identify user in PostHog:", error);
        }
    }
    /**
     * Reset PostHog (called on logout)
     */
    reset() {
        if (!this._isInitialized || !this.posthogInstance) {
            return;
        }
        try {
            this.posthogInstance.reset();
            logger.debug("PostHog reset");
        }
        catch (error) {
            logger.debug("Failed to reset PostHog:", error);
        }
    }
    /**
     * Check if PostHog is initialized
     */
    isInitialized() {
        return this._isInitialized;
    }
    /**
     * Capture a custom event
     */
    capture(eventName, properties) {
        if (!this._isInitialized || !this.posthogInstance) {
            return;
        }
        try {
            this.posthogInstance.capture(eventName, properties);
        }
        catch (error) {
            logger.debug("Failed to capture event in PostHog:", error);
        }
    }
    /**
     * Capture an exception explicitly (for errors caught in try/catch)
     */
    captureException(error, properties) {
        if (!this._isInitialized || !this.posthogInstance) {
            return;
        }
        try {
            this.posthogInstance.captureException(error instanceof Error ? error : new Error(String(error)), properties);
        }
        catch (err) {
            logger.debug("Failed to capture exception in PostHog:", err);
        }
    }
    /**
     * Get the PostHog instance (for advanced usage)
     */
    getPostHogInstance() {
        return this.posthogInstance;
    }
}
// Export singleton instance
export const postHogBOService = PostHogBOService.getInstance();
